import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { Field, Image, ImageField, TextField } from '@sitecore-jss/sitecore-jss-nextjs';
import useEmblaCarousel from 'embla-carousel-react';
import { sanitize } from 'isomorphic-dompurify';

import tailwindConfig from '@gn-audio/jss-components/tailwind-presets/jabra-preset';
import { HexColor, isColorBright, resolveColorProperty } from '@gn-audio/jss-components/utils';

import ArrowSliderNavigation from 'common/components/JabraSlider/ArrowSliderNavigation';
import { classNames } from 'common/utils/classNames';
import { useBreakpoint } from 'utils/hooks/useBreakpoint';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

export type ProductItem = {
  productUrl: TextField;
  productImage: ImageField;
  productName: TextField;
};

export type VideoBannerProducts = {
  withProductsSection: Field<boolean>;
  productsList: ProductItem[];
  explanatoryText: Field<string>;
  explanatoryBackgroundColor: Field<string>;
};

export const VideoBannerProducts = (props: VideoBannerProducts) => {
  const { getURLWithLocalePrefix } = useFallbackTranslation();
  const { withProductsSection, productsList, explanatoryText, explanatoryBackgroundColor } = props;
  const { isAboveSm } = useBreakpoint('sm', true);
  const { isAboveXl } = useBreakpoint('xl', true);

  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const totalSlides = emblaApi?.slideNodes().length || 0;
  const containerWith = totalSlides > 3 ? '' : 'lg:w-3/6';
  const multipleSlidesClassName =
    totalSlides > 3 ? 'lg:w-1/4 lg:basis-1/4 xl:w-1/5 xl:basis-1/5' : '';

  const canScrollNext = emblaApi?.canScrollNext() ?? false;
  const canScrollPrev = emblaApi?.canScrollPrev() ?? false;

  const sliderCentered = useMemo(() => {
    if (isAboveXl && productsList.length < 5) {
      return true;
    } else if (isAboveSm && productsList.length < 3) {
      return true;
    }

    return false;
  }, [isAboveXl, isAboveSm, productsList.length]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  if (!withProductsSection.value) {
    return null;
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      emblaApi?.scrollPrev();
    } else if (event.key === 'ArrowRight') {
      emblaApi?.scrollNext();
    }
  };

  const defaultBackgroundColor = tailwindConfig.theme.colors.yellow as HexColor;
  const explanatoryBackgroundColorHex = !!explanatoryBackgroundColor?.value
    ? resolveColorProperty(explanatoryBackgroundColor.value) ?? defaultBackgroundColor
    : defaultBackgroundColor;
  const explanatoryTextClassName = explanatoryBackgroundColor?.value
    ? isColorBright(explanatoryBackgroundColorHex)
      ? 'text-black'
      : 'text-white'
    : 'text-white';

  return (
    <div
      className="absolute -bottom-[48px] z-10 flex w-full justify-center"
      onKeyDown={handleKeyDown}
    >
      <div className={`relative flex w-3/4 flex-col justify-end ${containerWith}`}>
        <div
          className="h-full w-full overflow-hidden rounded-t-3xl bg-[rgba(0,0,0,0.4)] py-4 backdrop-blur-md"
          ref={emblaRef}
        >
          <div className={classNames('flex', sliderCentered && 'justify-center')}>
            {productsList.map((el) => (
              <a
                href={getURLWithLocalePrefix(el.productUrl.value as string)}
                className={`group flex w-full shrink-0 grow-0 basis-full cursor-pointer flex-col items-center rounded-md no-underline sm:w-1/2 sm:basis-1/2 md:w-1/3 md:basis-1/3 ${multipleSlidesClassName}`}
                key={`video-banner-product-${el.productName.value}`}
              >
                <Image field={el.productImage} className="h-[196px] w-[196px]" />
                <div className="rounded-full bg-gray-700 px-4 py-2 text-center text-base font-bold text-white shadow-sm transition-colors duration-300 group-hover:bg-yellow group-hover:text-black">
                  {el.productName.value}
                </div>
              </a>
            ))}
          </div>
        </div>
        <ArrowSliderNavigation
          buttonVariant="primary"
          arrowsWrapperClass="!p-0 w-full h-full pointer-events-none"
          total={totalSlides}
          hideIfCantScroll={true}
          canScrollNext={canScrollNext}
          canScrollPrev={canScrollPrev}
          prevButtonClasses="absolute top-1/2 -translate-y-1/2 -left-6 pointer-events-auto z-30"
          nextButtonClasses="absolute top-1/2 -translate-y-1/2 -right-6 pointer-events-auto z-30"
          activeIndex={selectedIndex}
          onNext={() => {
            emblaApi?.scrollNext();
          }}
          onPrev={() => {
            emblaApi?.scrollPrev();
          }}
        />
        <p
          className={classNames(
            'flex min-h-12 w-full items-center justify-center rounded-b-3xl bg-yellow text-center font-bold',
            explanatoryTextClassName
          )}
          dangerouslySetInnerHTML={{ __html: sanitize(explanatoryText?.value) }}
          style={{
            backgroundColor: explanatoryBackgroundColorHex,
          }}
        />
      </div>
    </div>
  );
};
